import { TriageQueueRows } from './TriageQueueRows';

import { DealerAssignedTable, VerifiedSourceFilter, APP_TYPE_OPTIONS, SEGMENT_OPTIONS } from './triageDeskShared';
import { useTriageController } from './useTriageController';
export function TriageQueueTable({ ctx }: { ctx: ReturnType<typeof useTriageController> }) {
const { hscrollRef, dragRef, suppressClickRef, tab, rows, loading, error, dealerNotifiedPendingIds, filters, setFilters, sortKey, setSortKey, filtersOpen, setLeadsRefresh, openPicker, submitDealerNotified } = ctx;
return (<>
      {tab !== 'hot_overdue' && filtersOpen && (
        <div className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50 flex flex-wrap gap-3 items-center">
          {/* Application type chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Application:</span>
            {APP_TYPE_OPTIONS.map((s) => {
              const active = filters.applicationTypes.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    applicationTypes: active ? filters.applicationTypes.filter((x) => x !== s) : [...filters.applicationTypes, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* B2B/B2C chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Segment:</span>
            {SEGMENT_OPTIONS.map((s) => {
              const active = filters.businessSegments.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    businessSegments: active ? filters.businessSegments.filter((x) => x !== s) : [...filters.businessSegments, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* Location */}
          <label className="text-xs flex items-center gap-1">
            <span className="text-gray-500">Location:</span>
            <input
              type="text"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="e.g. Panipat"
              className="text-xs border border-gray-300 rounded px-2 py-1 w-32"
            />
          </label>
          {/* Verified source */}
          <select
            value={filters.verifiedSource}
            onChange={(e) => setFilters({ ...filters, verifiedSource: e.target.value as VerifiedSourceFilter })}
            className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            title="Verified by"
          >
            <option value="any">Verified by: any</option>
            <option value="chatkart">ChatKart (Ananya)</option>
            <option value="manual">Manual (rep call)</option>
          </select>
        </div>
      )}

      {error && (
        <div role="alert" className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error} <button onClick={() => setLeadsRefresh(n => n + 1)}>Retry</button>
        </div>
      )}

      {/* Table.
          Windows-mouse horizontal scrolling (2026-06-11): mouse wheels only
          scroll vertically, so a wide table was effectively unscrollable
          without a trackpad. Three affordances:
            1. drag-to-pan — press-and-drag any empty spot of the table
            2. always-visible styled horizontal scrollbar (12px hit target)
            3. vertical wheel translates to horizontal scroll while hovering
               the table whenever the PAGE has nothing left to scroll for that
               wheel direction (Shift+wheel stays native)
          Drag suppresses the click that would otherwise fire on row buttons. */}
      <style>{`
        .triage-hscroll { scrollbar-width: auto; scrollbar-color: #94a3b8 #f1f5f9; }
        .triage-hscroll::-webkit-scrollbar { height: 12px; }
        .triage-hscroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; border: 2px solid #f1f5f9; }
        .triage-hscroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        .triage-hscroll::-webkit-scrollbar-track { background: #f1f5f9; }
      `}</style>
      <div
        hidden={!!error && rows.length === 0}
        ref={hscrollRef}
        className="bg-white border border-gray-200 rounded-lg overflow-x-auto triage-hscroll"
        style={{ cursor: 'grab' }}
        onPointerDown={(e) => {
          // Left button only; never hijack interactive elements.
          if (e.button !== 0) return;
          const target = e.target as HTMLElement;
          if (target.closest('button, select, input, a, textarea')) return;
          const el = hscrollRef.current;
          if (!el) return;
          dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft, moved: false };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          const el = hscrollRef.current;
          if (!d || !el) return;
          const dx = e.clientX - d.startX;
          if (!d.moved && Math.abs(dx) < 6) return; // dead zone — keep clicks working
          if (!d.moved) {
            d.moved = true;
            el.setPointerCapture?.(e.pointerId);
            el.style.cursor = 'grabbing';
          }
          el.scrollLeft = d.startLeft - dx;
        }}
        onPointerUp={(e) => {
          const el = hscrollRef.current;
          if (el) {
            el.releasePointerCapture?.(e.pointerId);
            el.style.cursor = 'grab';
          }
          // Keep `moved` until the click event fires (click follows pointerup).
          const d = dragRef.current;
          dragRef.current = null;
          if (d?.moved) {
            suppressClickRef.current = true;
            setTimeout(() => { suppressClickRef.current = false; }, 0);
          }
        }}
        onPointerCancel={() => {
          const el = hscrollRef.current;
          if (el) el.style.cursor = 'grab';
          dragRef.current = null;
        }}
        onClickCapture={(e) => {
          if (suppressClickRef.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onWheel={(e) => {
          const el = hscrollRef.current;
          if (!el) return;
          // Shift+wheel already scrolls horizontally natively. Translate a
          // plain vertical wheel into horizontal pan only when the table can
          // still move that way — never trap the user's page scroll.
          if (e.shiftKey || e.deltaX !== 0 || e.deltaY === 0) return;
          const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
          const canLeft = el.scrollLeft > 0;
          const doc = document.documentElement;
          const pageCanScroll = e.deltaY > 0
            ? window.scrollY + window.innerHeight < doc.scrollHeight - 1
            : window.scrollY > 0;
          if (pageCanScroll) return; // page motion wins while it has room
          // No preventDefault — React wheel listeners are passive, and the
          // page has no scroll room left here anyway (checked above).
          if ((e.deltaY > 0 && canRight) || (e.deltaY < 0 && canLeft)) {
            el.scrollLeft += e.deltaY;
          }
        }}
      >
        {tab === 'dealer_assigned' ? (
          <DealerAssignedTable
            rows={rows}
            loading={loading}
            pendingIds={dealerNotifiedPendingIds}
            onReassign={(row) => openPicker(row, 'reassign')}
            onMarkSent={(row) => void submitDealerNotified(row)}
          />
        ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Sl. No.</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'newest' ? 'oldest' : 'newest')}
              >
                Enquiry Date {sortKey === 'newest' ? '↓' : sortKey === 'oldest' ? '↑' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'source_asc' ? 'source_desc' : 'source_asc')}
              >
                Enquiry Source {sortKey === 'source_asc' ? '↑' : sortKey === 'source_desc' ? '↓' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'name_asc' ? 'name_desc' : 'name_asc')}
              >
                Customer Name {sortKey === 'name_asc' ? '↑' : sortKey === 'name_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Contact Number</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'location_asc' ? 'location_desc' : 'location_asc')}
              >
                Location / City {sortKey === 'location_asc' ? '↑' : sortKey === 'location_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Requirement</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Application Type</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">B2B / B2C</th>
              {/* ── Avika (2026-06-10) columns ─────────────────────────────── */}
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Line</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Capacity</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Timeline</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Price shared</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
              >
                Lead Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Tier</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Responses</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Copy</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Age</th><th className="text-left px-3 py-2 font-semibold text-gray-700">Verified</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">
                {tab === 'unassigned' || tab === 'hot_overdue' || tab === 'b2b'
                  ? 'Assign To Member'
                  : tab === 'assigned' ? 'Assigned To' : 'Created By'}
              </th>
            </tr>
          </thead>
          <TriageQueueRows ctx={ctx} />
        </table>
        )}
      </div>

      {/* Pagination — page size (20/40/60/80/100) + offset paging.
          Hidden on the Overdue-HOT tab (server caps the breach feed). */}
      </>);
}
